import React, { useState } from 'react';
import { Table, Descriptions, Space, Button, Popconfirm, Tag } from 'antd';
import { Link, useRouteMatch } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { Can } from '../Shared';
import { Client, CLIENT_STATUS } from '../../types/Client';
import { FORM_MODE } from '../../types/Form';

interface ClientListInterfaceProps {
    wrapperStyleClass?: string;
    clients: Client[];
    totalClients: number;
    onDeleteClient(clientId: number): Promise<void>;
    rbacRules?: { userRole: string | null; permissions: any } | undefined;
}

const ClientList: React.FC<ClientListInterfaceProps> = (props: ClientListInterfaceProps): React.ReactElement => {
    const { wrapperStyleClass, clients, totalClients, onDeleteClient, rbacRules } = props;

    const routeMatch = useRouteMatch();
    const [isPerformingDeleteOperation, setIsPerformingDeleteOperation] = useState<boolean>(false);

    const deleteClient = async (clientId: number): Promise<void> => {
        try {
            setIsPerformingDeleteOperation(true);
            await onDeleteClient(clientId);
        } finally {
            setIsPerformingDeleteOperation(false);
        }
    };

    const clientConfig: { [key: string]: { tagStatusColor: string; isEditable: boolean; isRemovable: boolean } } = {
        [CLIENT_STATUS.ACTIVE]: {
            tagStatusColor: 'blue',
            isEditable: true,
            isRemovable: true,
        },
        [CLIENT_STATUS.DELETED]: {
            tagStatusColor: 'red',
            isEditable: false,
            isRemovable: false,
        },
    };

    const columns: ColumnsType<Client> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            responsive: ['xs', 'sm', 'md'],
            render(clientName: string, client: Client): JSX.Element {
                return (
                    <Link
                        to={`${routeMatch.path}/${FORM_MODE.DETAIL}/${client.id}`}
                        id="client-list__btn-view-detail"
                        title="View detail"
                    >
                        {clientName}
                    </Link>
                );
            },
        },
        {
            title: 'Stuart Client Id',
            dataIndex: 'stuartClientId',
            key: 'stuartClientId',
            width: '20%',
            responsive: ['md'],
        },
        {
            title: 'API Client Id',
            dataIndex: 'apiClientId',
            key: 'apiClientId',
            width: '25%',
            responsive: ['md'],
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: '10%',
            responsive: ['md'],
            render(text: string): JSX.Element {
                return (
                    <Tag color={clientConfig[text].tagStatusColor} className="m-0" title={text}>
                        <small>{text}</small>
                    </Tag>
                );
            },
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            width: '25%',
            responsive: ['xs', 'sm', 'md'],
            render(text: string, client: Client): JSX.Element {
                return (
                    <Space size="middle">
                        <Button
                            type="link"
                            id="client-list__btn-edit"
                            name="client-list__btn-edit"
                            title="Edit"
                            disabled={isPerformingDeleteOperation || !clientConfig[client.status].isEditable}
                        >
                            <Link to={`${routeMatch.path}/${FORM_MODE.EDIT}/${client.id}`}>Edit</Link>
                        </Button>
                        <Button
                            type="link"
                            id="client-list__btn-copy-config"
                            name="client-list__btn-copy-config"
                            title="Copy Config"
                            disabled={isPerformingDeleteOperation || !clientConfig[client.status].isEditable}
                        >
                            <Link to={`${routeMatch.path}/copy-config/${client.id}`}>Copy Config</Link>
                        </Button>
                        <Can
                            role={rbacRules?.userRole}
                            perform={rbacRules?.permissions.CLIENT_DELETE}
                            yes={(): React.ReactElement => (
                                <Popconfirm
                                    placement="leftTop"
                                    id={`pop-confirm-${client.id}`}
                                    title={`Are you sure you want to delete client ${client.name}？`}
                                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                    onConfirm={(): Promise<void> => deleteClient(client.id)}
                                    okText="Yes"
                                    cancelText="No"
                                    disabled={isPerformingDeleteOperation || !clientConfig[client.status].isRemovable}
                                >
                                    <Button
                                        type="link"
                                        className="text-danger"
                                        id="client-list__btn-delete"
                                        name="client-list__btn-delete"
                                        title="Delete"
                                        disabled={
                                            isPerformingDeleteOperation || !clientConfig[client.status].isRemovable
                                        }
                                    >
                                        Delete
                                    </Button>
                                </Popconfirm>
                            )}
                            no={(): React.ReactElement => <span />}
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <div className={wrapperStyleClass || 'px-4 pb-4 bg-white'}>
            <Descriptions size="small">
                <Descriptions.Item label={<span className="text-black-50">N&deg; registered clients</span>}>
                    <span className="text-black-50">{totalClients}</span>
                </Descriptions.Item>
            </Descriptions>
            {clients && <Table columns={columns} dataSource={clients} tableLayout="fixed" pagination={false} />}
        </div>
    );
};

export default ClientList;
